import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import { schoolAdminNavItems } from '../../constants/navigation';
import EmptyState from '../../components/Shared/EmptyState';

const PERIOD_OPTIONS = [
  { label: '7 Days', value: 7 },
  { label: '30 Days', value: 30 },
  { label: '90 Days', value: 90 },
];

const SchoolAdminAnalyticsPage = () => {
  const { currentUser } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiRequest(`/api/school-admin/analytics?days=${period}`, 'GET');
      setAnalytics(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleExportCSV = () => {
    if (!analytics) return;
    const rows = [['Metric', 'Value']];
    rows.push(['Total Students', analytics.total_students || 0]);
    rows.push(['Total Teachers', analytics.total_teachers || 0]);
    rows.push(['Active Students', analytics.active_students || 0]);
    rows.push(['Engagement Rate', `${analytics.engagement_rate || 0}%`]);
    rows.push(['Total Sessions', analytics.total_sessions || 0]);
    rows.push([]);
    if (analytics.teacher_comparison?.length) {
      rows.push(['Teacher Comparison']);
      rows.push(['Teacher', 'Students', 'Avg Engagement', 'Sessions']);
      analytics.teacher_comparison.forEach(t => {
        rows.push([t.teacher_name, t.student_count, t.avg_engagement, t.sessions_count]);
      });
    }
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `school_analytics_${period}d_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <DashboardLayout title="Analytics" navItems={schoolAdminNavItems} userRole="school-admin" userName={currentUser?.full_name || 'Admin'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading analytics...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title="Analytics" navItems={schoolAdminNavItems} userRole="school-admin" userName={currentUser?.full_name || 'Admin'}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading analytics: {error}
        </div>
      </DashboardLayout>
    );
  }

  const dailyActive = analytics?.daily_active_students || [];
  const teacherComparison = analytics?.teacher_comparison || [];
  const gradeBreakdown = analytics?.grade_breakdown || [];
  const adoptionCurve = analytics?.adoption_curve || [];
  const topTopics = analytics?.topic_trends?.top_topics || [];

  return (
    <DashboardLayout title="Analytics" navItems={schoolAdminNavItems} userRole="school-admin" userName={currentUser?.full_name || 'Admin'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-3xl font-bold text-gray-900">School Analytics</h2>
        <button onClick={handleExportCSV}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
          Export CSV
        </button>
      </div>

      {/* Period Selector */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6 w-fit">
        {PERIOD_OPTIONS.map((opt) => (
          <button key={opt.value} onClick={() => setPeriod(opt.value)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              period === opt.value ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}>
            {opt.label}
          </button>
        ))}
      </div>

      {/* Empty State */}
      {!analytics?.total_students && !analytics?.total_sessions && (
        <EmptyState
          icon="&#128200;"
          title="No Analytics Data Yet"
          description="Analytics appear after students start using Brillabee. Check back once your school has active users."
        />
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <SummaryCard label="Total Teachers" value={analytics?.total_teachers || 0} />
        <SummaryCard label="Total Students" value={analytics?.total_students || 0} />
        <SummaryCard label="Active Students" value={analytics?.active_students || 0} color="green" />
        <SummaryCard label="Engagement Rate" value={`${analytics?.engagement_rate || 0}%`} color="indigo" />
      </div>

      {/* Daily Active Students Chart */}
      {dailyActive.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Active Students</h3>
          <div className="flex items-end gap-0.5 h-40">
            {dailyActive.map((day, index) => {
              const maxVal = Math.max(...dailyActive.map(d => d.active_students), 1);
              const height = (day.active_students / maxVal) * 100;
              return (
                <div key={index} className="flex-1 group relative">
                  <div className="bg-indigo-400 hover:bg-indigo-600 rounded-t transition-all w-full"
                    style={{ height: `${Math.max(height, 2)}%` }} />
                  <div className="hidden group-hover:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                    {day.date}: {day.active_students} active
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>{dailyActive[0]?.date}</span>
            <span>{dailyActive[dailyActive.length - 1]?.date}</span>
          </div>
        </div>
      )}

      {/* Teacher Comparison */}
      {teacherComparison.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Teacher Comparison</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Teacher</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">Students</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">Avg Engagement</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">Sessions</th>
                </tr>
              </thead>
              <tbody>
                {teacherComparison.map((t) => (
                  <tr key={t.teacher_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium text-gray-900">{t.teacher_name}</td>
                    <td className="text-center py-2 px-3">{t.student_count}</td>
                    <td className="text-center py-2 px-3">
                      <span className={`font-semibold ${t.avg_engagement >= 60 ? 'text-green-600' : t.avg_engagement >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {t.avg_engagement}%
                      </span>
                    </td>
                    <td className="text-center py-2 px-3">{t.sessions_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Grade Breakdown */}
      {gradeBreakdown.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Grade Breakdown</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {gradeBreakdown.map((g) => (
              <div key={g.grade_level} className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 capitalize">{g.grade_level}</p>
                <p className="text-xl font-bold text-gray-900">{g.student_count} students</p>
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>Engagement: {g.avg_engagement}%</span>
                  <span>{g.sessions_count} sessions</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Adoption Curve */}
      {adoptionCurve.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Student Adoption Curve (90 days)</h3>
          <div className="flex items-end gap-0.5 h-32">
            {adoptionCurve.map((day, index) => {
              const maxVal = Math.max(...adoptionCurve.map(d => d.cumulative_students), 1);
              const height = (day.cumulative_students / maxVal) * 100;
              return (
                <div key={index} className="flex-1 group relative">
                  <div className="bg-green-400 hover:bg-green-600 rounded-t transition-all w-full"
                    style={{ height: `${Math.max(height, 2)}%` }} />
                  <div className="hidden group-hover:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                    {day.date}: {day.cumulative_students} students
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>{adoptionCurve[0]?.date}</span>
            <span>{adoptionCurve[adoptionCurve.length - 1]?.date}</span>
          </div>
        </div>
      )}

      {/* Topic Trends */}
      {topTopics.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Topics</h3>
          <div className="space-y-2">
            {topTopics.slice(0, 10).map((topic, idx) => {
              const maxCount = Math.max(...topTopics.map(t => t.count), 1);
              const pct = (topic.count / maxCount) * 100;
              return (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-32 text-sm text-gray-700 font-medium truncate">{topic.topic}</div>
                  <div className="flex-1 bg-gray-100 rounded-full h-4">
                    <div className="bg-indigo-500 h-4 rounded-full flex items-center justify-end px-2"
                      style={{ width: `${Math.max(pct, 5)}%` }}>
                      <span className="text-xs text-white font-semibold">{topic.count}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

const SummaryCard = ({ label, value, color = 'gray' }) => {
  const colorClass = color === 'green' ? 'text-green-600' : color === 'indigo' ? 'text-indigo-600' : 'text-gray-900';
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <p className="text-sm text-gray-600">{label}</p>
      <p className={`text-2xl font-bold ${colorClass}`}>{value}</p>
    </div>
  );
};

export default SchoolAdminAnalyticsPage;
